import {useId} from 'react';
import {IS_GMK} from '@lib/brand';

interface Props {
  /** Размер квадратного SVG в px. */
  size?: number;
  /** Доп. классы (обычно для drop-shadow). */
  className?: string;
  /**
   * Цветовая схема. По умолчанию — белая (для тёмного/цветного фона).
   * `purple` — цветная для светлого фона: у Colver фиолетовая, у GMK — GMK BLUE.
   */
  tone?: 'white' | 'purple';
}

/**
 * «Выпуклая» AI-звёздочка — кастомный SVG с объёмным градиентом, ярким ядром,
 * тонкой кромкой и мягкой тенью. Намеренно НЕ плоская lucide-Sparkles.
 *
 * Composition: большая 4-лучевая звезда + малая искра + светящееся ядро-блик.
 */
export const SparkleAi = ({size = 16, className, tone = 'white'}: Props) => {
  const uid = useId().replace(/:/g, '');
  const gradId = `spark-${uid}-grad`;
  const coreId = `spark-${uid}-core`;
  const glowId = `spark-${uid}-glow`;

  // Большая звезда (4 луча) — единый path, переиспользуется для заливки и блика.
  const bigStar =
    'M12 1.5 L13.55 9.05 C13.75 10.2 14.6 11.05 15.75 11.25 L22.5 12 ' +
    'L15.75 12.75 C14.6 12.95 13.75 13.8 13.55 14.95 L12 22.5 ' +
    'L10.45 14.95 C10.25 13.8 9.4 12.95 8.25 12.75 L1.5 12 ' +
    'L8.25 11.25 C9.4 11.05 10.25 10.2 10.45 9.05 Z';

  // Палитра GMK: AI — «энергетические волны» GMK BLUE (фиолетового в бренде нет);
  // у оригинального Colver — фиолетовая.
  const c = !IS_GMK
    ? tone === 'white'
      ? {hi: '#ffffff', mid: '#f3f0ff', lo: '#ddd6fe', edge: '#c4b5fd', shadow: '#4c1d95'}
      : {hi: '#c4b5fd', mid: '#8b5cf6', lo: '#6d28d9', edge: '#5b21b6', shadow: '#3b0764'}
    : tone === 'white'
      ? {
          hi: '#ffffff',
          mid: '#eef6ff',
          lo: '#cfe5ff',
          edge: '#a9cdf5',
          shadow: '#1f4f8a',
        }
      : {
          hi: '#cfe5ff',
          mid: '#5e9be0',
          lo: '#2f6fbf',
          edge: '#245ea8',
          shadow: '#173f75',
        };

  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      className={className}
      aria-hidden='true'
    >
      <defs>
        {/* Диагональный градиент: светлый верх-лево → насыщенный низ-право (объём) */}
        <linearGradient id={gradId} x1='28%' y1='8%' x2='82%' y2='96%'>
          <stop offset='0%' stopColor={c.hi} />
          <stop offset='46%' stopColor={c.mid} />
          <stop offset='100%' stopColor={c.lo} />
        </linearGradient>
        {/* Яркое смещённое ядро — даёт ощущение выпуклости */}
        <radialGradient id={coreId} cx='40%' cy='34%' r='62%'>
          <stop offset='0%' stopColor='#ffffff' stopOpacity='0.95' />
          <stop offset='42%' stopColor='#ffffff' stopOpacity='0.32' />
          <stop offset='100%' stopColor='#ffffff' stopOpacity='0' />
        </radialGradient>
        <filter id={glowId} x='-35%' y='-35%' width='170%' height='170%'>
          <feDropShadow
            dx='0'
            dy='0.7'
            stdDeviation='0.7'
            floodColor={c.shadow}
            floodOpacity='0.4'
          />
        </filter>
      </defs>

      <g filter={`url(#${glowId})`}>
        {/* Большая звезда: заливка градиентом + тонкая кромка для чёткости */}
        <path
          d={bigStar}
          fill={`url(#${gradId})`}
          stroke={c.edge}
          strokeWidth='0.4'
          strokeOpacity='0.55'
          strokeLinejoin='round'
        />
        {/* Малая искра справа-сверху */}
        <path
          d='M19 2.4 L19.6 5.1 C19.72 5.6 20.05 5.93 20.55 6.05 L23.2 6.6 L20.55 7.15 C20.05 7.27 19.72 7.6 19.6 8.1 L19 10.8 L18.4 8.1 C18.28 7.6 17.95 7.27 17.45 7.15 L14.8 6.6 L17.45 6.05 C17.95 5.93 18.28 5.6 18.4 5.1 Z'
          fill={`url(#${gradId})`}
          opacity='0.92'
        />
      </g>

      {/* Ядро-блик поверх — основной «объём» */}
      <path d={bigStar} fill={`url(#${coreId})`} />
    </svg>
  );
};
